/**
 * SCR-023 — Bài tập & chấm bài (Teacher)
 * Route: /teacher/classes/:classId/assignments
 * Two views: assignment list → submission detail (grade panel).
 * Mobile: card list 1-col, dialogs 1-col, download buttons full-width.
 */
import { createFileRoute } from '@tanstack/react-router';
import {
  AlertCircle,
  ArrowLeft,
  BookOpen,
  Plus,
  Pencil,
  RefreshCw,
  Trash2,
} from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Textarea } from '@/components/ui/textarea';
import { SubmissionFileList } from '@client/components/teacher/submission-file-list';
import {
  useClassAssignments,
  useCreateAssignment,
  useUpdateAssignment,
  useDeleteAssignment,
  useAssignmentSubmissions,
  useGradeSubmission,
  useClassLessons,
  useCreateLesson,
  useTeacherClassDetail,
} from '@client/lib/queries/use-teacher';
import type { Assignment, Submission, Lesson } from '@client/lib/queries/use-teacher';
import { ApiError } from '@client/lib/api-client';

export const Route = createFileRoute('/teacher/classes/$classId/assignments')({
  component: AssignmentsTab,
});

// ---------------------------------------------------------------------------
const ASSIGNMENT_TYPE_META: Record<string, { label: string; variant: 'info' | 'warning' | 'secondary' }> = {
  homework: { label: 'Bài tập',  variant: 'info' },
  quiz:     { label: 'Quiz',     variant: 'warning' },
  project:  { label: 'Dự án',   variant: 'secondary' },
};

const SUBMISSION_STATUS_META: Record<string, { label: string; variant: 'outline' | 'info' | 'success' | 'destructive' }> = {
  assigned:  { label: 'Chưa nộp', variant: 'outline' },
  submitted: { label: 'Đã nộp',  variant: 'info' },
  graded:    { label: 'Đã chấm', variant: 'success' },
  late:      { label: 'Nộp trễ', variant: 'destructive' },
};

// ---------------------------------------------------------------------------
function AssignmentsTab() {
  const { classId } = Route.useParams();
  const { data: cls } = useTeacherClassDetail(classId);
  const { data: assignments, isLoading, isError, refetch } = useClassAssignments(classId);
  const { data: lessons } = useClassLessons(classId);

  const [selectedAssignment, setSelectedAssignment] = useState<Assignment | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [editAssignment, setEditAssignment] = useState<Assignment | null>(null);
  const [lessonDialogOpen, setLessonDialogOpen] = useState(false);

  const isReadOnly = cls?.status === 'cancelled' || cls?.status === 'finished';
  const lessonList = Array.isArray(lessons) ? lessons : [];

  const deleteAssignment = useDeleteAssignment(classId);

  async function handleDeleteAssignment(id: string) {
    if (!confirm('Bạn chắc chắn muốn xóa bài tập này?')) return;
    try {
      await deleteAssignment.mutateAsync(id);
      toast.success('Đã xóa bài tập.');
      if (selectedAssignment?.id === id) setSelectedAssignment(null);
    } catch (err) {
      const msg = err instanceof ApiError && err.status === 409
        ? 'Bài tập đã có bài nộp, không thể xóa.'
        : 'Lỗi xóa bài tập.';
      toast.error(msg);
    }
  }

  // ---------------------------------------------------------------------------
  // Submission detail view
  if (selectedAssignment) {
    return (
      <SubmissionView
        assignment={selectedAssignment}
        readOnly={isReadOnly}
        onBack={() => setSelectedAssignment(null)}
      />
    );
  }

  // ---------------------------------------------------------------------------
  // Assignment list view
  return (
    <div className="space-y-4">
      {/* Toolbar */}
      {!isReadOnly && (
        <div className="flex flex-col gap-2 sm:flex-row">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setLessonDialogOpen(true)}
            className="min-h-[44px] w-full sm:w-auto"
          >
            <BookOpen className="mr-1.5 h-4 w-4" />
            Quản lý buổi học
          </Button>
          <Button
            size="sm"
            onClick={() => setCreateOpen(true)}
            className="min-h-[44px] w-full sm:w-auto"
          >
            <Plus className="mr-1.5 h-4 w-4" />
            Bài tập mới
          </Button>
        </div>
      )}

      {/* Loading */}
      {isLoading && (
        <div className="space-y-3">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-24 rounded-xl" />
          ))}
        </div>
      )}

      {/* Error */}
      {isError && !isLoading && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription className="flex items-center justify-between gap-4">
            <span>Không tải được danh sách bài tập.</span>
            <Button variant="outline" size="sm" onClick={() => void refetch()}>
              <RefreshCw className="mr-1.5 h-3.5 w-3.5" />
              Thử lại
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {/* Empty */}
      {!isLoading && !isError && (!assignments || (Array.isArray(assignments) ? assignments : []).length === 0) && (
        <div className="flex flex-col items-center gap-3 py-12 text-center">
          <BookOpen className="h-10 w-10 text-muted-foreground/40" />
          <p className="text-sm text-muted-foreground">
            Lớp chưa có bài tập.{!isReadOnly && ' Bấm "+ Bài tập mới" để tạo.'}
          </p>
        </div>
      )}

      {/* Assignment cards */}
      {!isLoading && !isError && assignments && (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {(Array.isArray(assignments) ? assignments : []).map((a) => (
            <AssignmentCard
              key={a.id}
              assignment={a}
              readOnly={isReadOnly}
              onOpen={() => setSelectedAssignment(a)}
              onEdit={() => setEditAssignment(a)}
              onDelete={() => void handleDeleteAssignment(a.id)}
            />
          ))}
        </div>
      )}

      {/* Dialogs */}
      {createOpen && (
        <AssignmentDialog
          classId={classId}
          lessons={lessonList}
          onClose={() => setCreateOpen(false)}
        />
      )}
      {editAssignment && (
        <AssignmentDialog
          classId={classId}
          assignment={editAssignment}
          lessons={lessonList}
          onClose={() => setEditAssignment(null)}
        />
      )}
      {lessonDialogOpen && (
        <LessonDialog
          classId={classId}
          lessons={lessonList}
          onClose={() => setLessonDialogOpen(false)}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Assignment card
// ---------------------------------------------------------------------------
function AssignmentCard({
  assignment: a,
  readOnly,
  onOpen,
  onEdit,
  onDelete,
}: {
  assignment: Assignment;
  readOnly: boolean;
  onOpen: () => void;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const typeMeta = ASSIGNMENT_TYPE_META[a.type] ?? { label: a.type, variant: 'secondary' as const };
  const dueLabel = a.dueDate
    ? new Date(a.dueDate).toLocaleDateString('vi-VN')
    : 'Không có hạn';

  return (
    <Card className="rounded-xl border border-border hover:shadow-md transition-shadow">
      <CardContent className="p-4 space-y-2">
        <div className="flex items-start justify-between gap-2">
          <button
            onClick={onOpen}
            className="flex-1 text-left text-sm font-semibold text-foreground leading-snug hover:text-primary transition-colors"
          >
            {a.title}
          </button>
          <Badge variant={typeMeta.variant} className="text-xs shrink-0">
            {typeMeta.label}
          </Badge>
        </div>

        {a.lesson && (
          <p className="text-xs text-muted-foreground truncate">
            📚 {a.lesson.title}
          </p>
        )}

        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <span>🗓 Hạn: {dueLabel}</span>
          {a.submittedCount !== undefined && a.totalCount !== undefined && (
            <Badge variant="outline" className="text-xs">
              {a.submittedCount}/{a.totalCount} đã nộp
            </Badge>
          )}
        </div>

        {!readOnly && (
          <div className="flex gap-2 pt-1">
            <Button
              variant="ghost"
              size="sm"
              onClick={onEdit}
              className="h-8 px-2 text-xs"
            >
              <Pencil className="h-3.5 w-3.5 mr-1" />
              Sửa
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={onDelete}
              className="h-8 px-2 text-xs text-destructive hover:text-destructive"
            >
              <Trash2 className="h-3.5 w-3.5 mr-1" />
              Xóa
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={onOpen}
              className="h-8 px-3 text-xs ml-auto"
            >
              Xem bài nộp
            </Button>
          </div>
        )}
        {readOnly && (
          <Button variant="outline" size="sm" onClick={onOpen} className="w-full h-8 text-xs">
            Xem bài nộp
          </Button>
        )}
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Submission view
// ---------------------------------------------------------------------------
function SubmissionView({
  assignment,
  readOnly,
  onBack,
}: {
  assignment: Assignment;
  readOnly: boolean;
  onBack: () => void;
}) {
  const { data, isLoading, isError, refetch } = useAssignmentSubmissions(assignment.id);

  const submissions: Submission[] = (() => {
    if (!data) return [];
    // api.get unwraps { data: T } → T, so data is already { assignment, items } or { items }
    const d = data as unknown as Record<string, unknown>;
    if (Array.isArray(d['items'])) return d['items'] as Submission[];
    return [];
  })();

  return (
    <div className="space-y-4">
      <button
        onClick={onBack}
        className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
      >
        <ArrowLeft className="h-4 w-4" />
        Danh sách bài tập
      </button>

      <div className="rounded-xl border border-border bg-card p-4 space-y-1">
        <h2 className="text-base font-semibold">{assignment.title}</h2>
        <div className="text-xs text-muted-foreground flex flex-wrap gap-x-3 gap-y-1">
          {assignment.type && (
            <Badge variant={ASSIGNMENT_TYPE_META[assignment.type]?.variant} className="text-xs">
              {ASSIGNMENT_TYPE_META[assignment.type]?.label ?? assignment.type}
            </Badge>
          )}
          {assignment.dueDate && (
            <span>Hạn: {new Date(assignment.dueDate).toLocaleDateString('vi-VN')}</span>
          )}
          {assignment.maxScore && <span>Thang điểm: {assignment.maxScore}</span>}
        </div>
        {assignment.description && (
          <p className="text-sm text-muted-foreground mt-2 whitespace-pre-wrap">{assignment.description}</p>
        )}
      </div>

      {isLoading && (
        <div className="space-y-3">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-28 rounded-xl" />
          ))}
        </div>
      )}

      {isError && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription className="flex items-center justify-between gap-4">
            <span>Không tải được danh sách bài nộp.</span>
            <Button variant="outline" size="sm" onClick={() => void refetch()}>
              <RefreshCw className="mr-1.5 h-3.5 w-3.5" />
              Thử lại
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {!isLoading && !isError && submissions.length === 0 && (
        <p className="text-sm text-muted-foreground py-6 text-center">
          Chưa có bài nộp.
        </p>
      )}

      {submissions.map((sub) => (
        <SubmissionCard
          key={sub.id}
          submission={sub}
          assignmentId={assignment.id}
          maxScore={assignment.maxScore ?? 100}
          readOnly={readOnly}
        />
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Submission card (grade + files)
// ---------------------------------------------------------------------------
function SubmissionCard({
  submission,
  assignmentId,
  maxScore,
  readOnly,
}: {
  submission: Submission;
  assignmentId: string;
  maxScore: number;
  readOnly: boolean;
}) {
  const gradeMut = useGradeSubmission(assignmentId);
  const [score, setScore] = useState(submission.grade?.score != null ? String(submission.grade.score) : '');
  const [feedback, setFeedback] = useState(submission.grade?.feedback ?? '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const statusMeta = SUBMISSION_STATUS_META[submission.status] ?? { label: submission.status, variant: 'outline' as const };

  async function handleGrade() {
    const num = parseFloat(score);
    if (isNaN(num) || num < 0 || num > maxScore) {
      setError(`Điểm phải từ 0–${maxScore}`);
      return;
    }
    setError('');
    setSaving(true);
    try {
      await gradeMut.mutateAsync({ submissionId: submission.id, score: num, feedback: feedback || undefined });
      toast.success('Đã chấm điểm.');
    } catch {
      toast.error('Lỗi chấm điểm.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="rounded-xl border border-border bg-card p-4 space-y-3">
      {/* Header */}
      <div className="flex items-start justify-between gap-2 flex-wrap">
        <div>
          <p className="text-sm font-medium">{submission.student.fullName}</p>
          <p className="text-xs font-mono text-muted-foreground">{submission.student.studentCode}</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Badge variant={statusMeta.variant} className="text-xs">{statusMeta.label}</Badge>
          {submission.submittedAt && (
            <span className="text-xs text-muted-foreground">
              {new Date(submission.submittedAt).toLocaleString('vi-VN')}
            </span>
          )}
        </div>
      </div>

      {/* Submitted content */}
      {submission.content && (
        <p className="text-sm text-muted-foreground whitespace-pre-wrap border-l-2 border-border pl-3">
          {submission.content}
        </p>
      )}

      {/* Files */}
      <SubmissionFileList submissionId={submission.id} files={submission.files} />

      {/* Grade section */}
      {!readOnly && submission.status !== 'assigned' && (
        <div className="space-y-2 pt-2 border-t border-border">
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Chấm điểm</p>
          <div className="flex items-center gap-2">
            <Input
              type="number"
              inputMode="decimal"
              min={0}
              max={maxScore}
              value={score}
              onChange={(e) => { setScore(e.target.value); setError(''); }}
              placeholder={`0–${maxScore}`}
              className={`h-9 w-24 text-center ${error ? 'border-destructive' : ''}`}
              disabled={saving}
            />
            <span className="text-xs text-muted-foreground">/ {maxScore}</span>
          </div>
          {error && <p className="text-xs text-destructive">{error}</p>}
          <Textarea
            value={feedback}
            onChange={(e) => setFeedback(e.target.value)}
            placeholder="Nhận xét..."
            className="text-xs h-16 resize-none"
            maxLength={500}
          />
          <Button
            size="sm"
            onClick={() => void handleGrade()}
            disabled={saving}
            className="min-h-[44px] w-full sm:w-auto"
          >
            {saving ? 'Đang lưu...' : 'Lưu điểm'}
          </Button>
        </div>
      )}

      {/* Show existing grade (read-only) */}
      {(readOnly || submission.status === 'assigned') && submission.grade?.score != null && (
        <div className="pt-2 border-t border-border text-xs text-muted-foreground">
          Điểm: <span className="font-semibold text-foreground">{submission.grade.score}</span>/{maxScore}
          {submission.grade.feedback && <p className="mt-1 italic">{submission.grade.feedback}</p>}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Assignment create/edit dialog
// ---------------------------------------------------------------------------
function AssignmentDialog({
  classId,
  assignment,
  lessons,
  onClose,
}: {
  classId: string;
  assignment?: Assignment;
  lessons: Lesson[];
  onClose: () => void;
}) {
  const create = useCreateAssignment(classId);
  const update = useUpdateAssignment(classId);
  const isEdit = !!assignment;

  const [title, setTitle] = useState(assignment?.title ?? '');
  const [description, setDescription] = useState(assignment?.description ?? '');
  const [type, setType] = useState(assignment?.type ?? 'homework');
  const [lessonId, setLessonId] = useState(assignment?.lessonId ?? '');
  const [dueDate, setDueDate] = useState(
    assignment?.dueDate ? assignment.dueDate.slice(0, 16) : '',
  );
  const [maxScore, setMaxScore] = useState(String(assignment?.maxScore ?? 100));
  const [error, setError] = useState('');

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!title.trim()) { setError('Nhập tiêu đề bài tập.'); return; }
    const ms = parseFloat(maxScore);
    if (isNaN(ms) || ms <= 0) { setError('Thang điểm phải > 0.'); return; }
    setError('');

    const payload = {
      title: title.trim(),
      description: description.trim() || undefined,
      type,
      lessonId: lessonId || undefined,
      dueDate: dueDate || undefined,
      maxScore: ms,
    };

    try {
      if (isEdit && assignment) {
        await update.mutateAsync({ id: assignment.id, ...payload });
        toast.success('Đã cập nhật bài tập.');
      } else {
        await create.mutateAsync(payload);
        toast.success('Đã tạo bài tập.');
      }
      onClose();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Lỗi lưu bài tập.');
    }
  }

  const isPending = create.isPending || update.isPending;

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="w-full max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Sửa bài tập' : 'Tạo bài tập mới'}</DialogTitle>
        </DialogHeader>
        <form onSubmit={(e) => void handleSubmit(e)} className="space-y-4 pt-2">
          <div className="space-y-1.5">
            <Label htmlFor="a-title">Tiêu đề *</Label>
            <Input id="a-title" value={title} onChange={(e) => setTitle(e.target.value)} maxLength={200} className="h-9" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="a-desc">Đề bài</Label>
            <Textarea id="a-desc" value={description} onChange={(e) => setDescription(e.target.value)} className="h-24 resize-none text-sm" maxLength={5000} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="a-type">Loại bài</Label>
            <Select value={type} onValueChange={setType}>
              <SelectTrigger id="a-type" className="h-9"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(ASSIGNMENT_TYPE_META).map(([v, m]) => (
                  <SelectItem key={v} value={v}>{m.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {lessons.length > 0 && (
            <div className="space-y-1.5">
              <Label htmlFor="a-lesson">Buổi học</Label>
              <Select
                value={lessonId ? lessonId : '__none__'}
                onValueChange={(v) => setLessonId(v === '__none__' ? '' : v)}
              >
                <SelectTrigger id="a-lesson" className="h-9"><SelectValue placeholder="Không gắn buổi" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">Không gắn buổi</SelectItem>
                  {lessons.map((l) => (
                    <SelectItem key={l.id} value={l.id}>Buổi {l.orderNo}: {l.title}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <div className="space-y-1.5">
            <Label htmlFor="a-due">Hạn nộp</Label>
            <Input id="a-due" type="datetime-local" value={dueDate} onChange={(e) => setDueDate(e.target.value)} className="h-9" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="a-max">Thang điểm tối đa</Label>
            <Input id="a-max" type="number" inputMode="decimal" min={1} value={maxScore} onChange={(e) => setMaxScore(e.target.value)} className="h-9 w-28" />
          </div>
          {error && <p className="text-xs text-destructive">{error}</p>}
          <div className="flex gap-2 pt-1">
            <Button type="button" variant="outline" className="flex-1" onClick={onClose}>Hủy</Button>
            <Button type="submit" className="flex-1" disabled={isPending}>
              {isPending ? 'Đang lưu...' : isEdit ? 'Cập nhật' : 'Tạo bài tập'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Lesson management dialog (CRUD)
// ---------------------------------------------------------------------------
function LessonDialog({
  classId,
  lessons,
  onClose,
}: {
  classId: string;
  lessons: Lesson[];
  onClose: () => void;
}) {
  const create = useCreateLesson(classId);
  const [title, setTitle] = useState('');
  const [scheduledDate, setScheduledDate] = useState('');
  const [error, setError] = useState('');

  const nextOrder = Math.max(0, ...lessons.map((l) => l.orderNo)) + 1;

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    if (!title.trim()) { setError('Nhập tiêu đề buổi học.'); return; }
    setError('');
    try {
      await create.mutateAsync({
        orderNo: nextOrder,
        title: title.trim(),
        scheduledDate: scheduledDate || undefined,
      });
      toast.success('Đã tạo buổi học.');
      setTitle('');
      setScheduledDate('');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Lỗi tạo buổi học.');
    }
  }

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="w-full max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Quản lý buổi học</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 pt-2">
          {/* Existing lessons */}
          {lessons.length > 0 && (
            <div className="space-y-2">
              {lessons.map((l) => (
                <div key={l.id} className="flex items-center justify-between rounded-lg border border-border px-3 py-2 text-sm gap-2">
                  <span className="font-medium">Buổi {l.orderNo}</span>
                  <span className="flex-1 truncate text-muted-foreground">{l.title}</span>
                  {l.scheduledDate && (
                    <span className="text-xs text-muted-foreground shrink-0">
                      {new Date(l.scheduledDate).toLocaleDateString('vi-VN')}
                    </span>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Create new lesson */}
          <form onSubmit={(e) => void handleCreate(e)} className="space-y-3 border-t border-border pt-3">
            <p className="text-sm font-medium">Thêm buổi học #{nextOrder}</p>
            <div className="space-y-1.5">
              <Label htmlFor="l-title">Tiêu đề *</Label>
              <Input id="l-title" value={title} onChange={(e) => setTitle(e.target.value)} maxLength={200} className="h-9" placeholder={`Buổi ${nextOrder}...`} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="l-date">Ngày dự kiến</Label>
              <Input id="l-date" type="date" value={scheduledDate} onChange={(e) => setScheduledDate(e.target.value)} className="h-9" />
            </div>
            {error && <p className="text-xs text-destructive">{error}</p>}
            <div className="flex gap-2">
              <Button type="button" variant="outline" className="flex-1" onClick={onClose}>Đóng</Button>
              <Button type="submit" className="flex-1" disabled={create.isPending}>
                {create.isPending ? 'Đang tạo...' : 'Thêm buổi học'}
              </Button>
            </div>
          </form>
        </div>
      </DialogContent>
    </Dialog>
  );
}
